import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { createAndFireEvent, withAnalyticsEvents, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { UI_EVENT_TYPE, OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { hasLoaded, Status } from '../../providers/as-data-provider';
import { UserSiteDataError } from '../errors/user-site-data-error';
import { getMetricBucket, RenderTrackerBuckets, ImageLoadTrackerBuckets } from '../get-metric-bucket';
import { performanceNow } from '../performance-now';
export const NAVIGATION_CHANNEL = 'navigation';
export const SWITCHER_SUBJECT = 'atlassianSwitcher';
export const SWITCHER_ITEM_SUBJECT = 'atlassianSwitcherItem';
export const SWITCHER_ITEM_LOZENGES_SUBJECT = 'atlassianSwitcherItemLozenges';
export const SWITCHER_CHILD_ITEM_SUBJECT = 'atlassianSwitcherChildItem';
export const SWITCHER_ITEM_EXPAND_SUBJECT = 'atlassianSwitcherItemExpand';
export const SWITCHER_COMPONENT = 'atlassianSwitcher';
export const SWITCHER_SOURCE = 'atlassianSwitcher';
export const TRIGGER_COMPONENT = 'atlassianSwitcherPrefetch';
export const TRIGGER_SUBJECT = 'atlassianSwitcherPrefetch';
export const SWITCHER_PARTIAL_RESULTS = 'atlassianSwitcherPartialResultError';
export const SWITCHER_ORG_ITEM = 'atlassianSwitcherOrgItem';
export const SWITCHER_FOOTER_LEAVE_FEEDBACK = 'atlassianSwitcherFooterLeaveFeedback';
export const SWITCHER_ORG_NAME_EXPAND_PANEL = 'atlassianSwitcherOrgNameExpandPanel';
export const SWITCHER_CURRENT_ORG = 'atlassianSwitcherCurrentOrg';
export const SWITCHER_ORG_LIST = 'atlassianSwitcherOrganisationsList';
export const SWITCHER_JOINABLE_SITES = 'atlassianSwitcherJoinableSites';
export const SWITCHER_AVAILABLE_PRODUCTS = 'atlassianSwitcherAvailableProducts';
const SWITCHER_CUSTOM_LINKS = 'atlassianSwitcherCustomLinks';
const SWITCHER_REMOTE_ICON = 'atlassianSwitcherRemoteIcon';
const RENDERED_ACTION = 'rendered';
const NOT_RENDERED_ACTION = 'not_rendered';
const VIEWED_ACTION = 'viewed';
const LOADED_ACTION = 'loaded';
const NOT_LOADED_ACTION = 'not_loaded';
export const createAndFireNavigationEvent = createAndFireEvent(NAVIGATION_CHANNEL);
export const analyticsAttributes = attributes => ({
  attributes
});
export const withAnalyticsContextData = function (mapPropsToContext) {
  return function (WrappedComponent) {
    return props => /*#__PURE__*/React.createElement(NavigationAnalyticsContext, {
      data: mapPropsToContext(props)
    }, /*#__PURE__*/React.createElement(WrappedComponent, props));
  };
};
const isValidDuration = duration => {
  return duration !== null && duration !== undefined && duration >= 0;
};
export const RenderTracker = withAnalyticsEvents({
  onRender: (createAnalyticsEvent, props) => {
    var _props$data;
    const duration = (_props$data = props.data) === null || _props$data === void 0 ? void 0 : _props$data.duration;
    return createAnalyticsEvent({
      eventType: OPERATIONAL_EVENT_TYPE,
      action: RENDERED_ACTION,
      actionSubject: props.subject,
      attributes: {
        ...props.data,
        ...(isValidDuration(duration) && {
          bucket: getMetricBucket(duration, RenderTrackerBuckets)
        })
      }
    }).fire(NAVIGATION_CHANNEL);
  }
})(class extends React.Component {
  componentDidMount() {
    this.props.onRender();
  }
  render() {
    return null;
  }
});
export const NotRenderedTracker = withAnalyticsEvents({
  onRender: (createAnalyticsEvent, props) => {
    return createAnalyticsEvent({
      eventType: OPERATIONAL_EVENT_TYPE,
      action: NOT_RENDERED_ACTION,
      actionSubject: props.subject,
      attributes: props.data
    }).fire(NAVIGATION_CHANNEL);
  }
})(class extends React.Component {
  componentDidMount() {
    this.props.onRender();
  }
  render() {
    return null;
  }
});
export const ViewedTracker = withAnalyticsEvents({
  onRender: (createAnalyticsEvent, props) => {
    return createAnalyticsEvent({
      eventType: UI_EVENT_TYPE,
      action: VIEWED_ACTION,
      actionSubject: props.subject,
      attributes: props.data
    }).fire(NAVIGATION_CHANNEL);
  }
})(class extends React.Component {
  componentDidMount() {
    this.props.onRender();
  }
  render() {
    return null;
  }
});
export const ImageLoadedTracker = props => {
  const {
    onLoad,
    onError,
    ...imgProps
  } = props;
  const {
    createAnalyticsEvent
  } = useAnalyticsEvents();
  const imageMountedAt = React.useRef(performanceNow());
  const analyticsHandler = React.useCallback((actionType, event, action) => {
    const duration = performanceNow() - imageMountedAt.current;
    createAnalyticsEvent({
      action: actionType,
      actionSubject: SWITCHER_REMOTE_ICON,
      attributes: {
        ...(isValidDuration(duration) && {
          bucket: getMetricBucket(duration, ImageLoadTrackerBuckets),
          duration
        }),
        src: imgProps.src
      },
      eventType: OPERATIONAL_EVENT_TYPE
    }).fire(NAVIGATION_CHANNEL);
    if (action) {
      action(event);
    }
  }, [createAnalyticsEvent, imageMountedAt, imgProps.src]);
  const onErrorAnalyticsHandler = React.useCallback(errorEvent => {
    analyticsHandler(NOT_LOADED_ACTION, errorEvent, onError);
  }, [analyticsHandler, onError]);
  const onLoadAnalyticsHandler = React.useCallback(loadEvent => {
    analyticsHandler(LOADED_ACTION, loadEvent, onLoad);
  }, [analyticsHandler, onLoad]);

  // eslint-disable-next-line jsx-a11y/alt-text
  return /*#__PURE__*/React.createElement("img", _extends({}, imgProps, {
    onError: onErrorAnalyticsHandler,
    onLoad: onLoadAnalyticsHandler
  }));
};
const renderTrackerWithReason = ({
  subject,
  notRenderedReason,
  emptyRenderExpected,
  data
}) => {
  if (notRenderedReason) {
    return /*#__PURE__*/React.createElement(NotRenderedTracker, {
      subject: subject,
      data: {
        ...data,
        notRenderedReason
      }
    });
  }
  return /*#__PURE__*/React.createElement(RenderTracker, {
    subject: subject,
    data: {
      ...data,
      emptyRender: emptyRenderExpected
    }
  });
};
const renderTracker = ({
  subject,
  providerFailed,
  emptyRenderExpected,
  linksRendered,
  data
}) => {
  if (providerFailed || linksRendered.length === 0 && !emptyRenderExpected) {
    return /*#__PURE__*/React.createElement(NotRenderedTracker, {
      subject: subject,
      data: {
        ...data,
        providerFailed
      }
    });
  }
  return /*#__PURE__*/React.createElement(RenderTracker, {
    subject: subject,
    data: {
      ...data,
      emptyRender: emptyRenderExpected
    }
  });
};
export const getJoinableSitesRenderTracker = (providerResult, joinableSiteLinks, data) => {
  if (!hasLoaded(providerResult)) {
    return null;
  }
  if (providerResult.data && !providerResult.data.sites) {
    return null;
  }

  // The render is considered failed when either the provider failed, or the provider returned a non-empty result but nothing was rendered
  const emptyRenderExpected = Boolean(providerResult.data && providerResult.data.sites && providerResult.data.sites.length === 0);
  return renderTracker({
    subject: SWITCHER_JOINABLE_SITES,
    providerFailed: providerResult.data === null,
    emptyRenderExpected,
    linksRendered: joinableSiteLinks,
    data
  });
};
export const getCustomLinksRenderTracker = (customLinksProviderResult, userSiteDataProviderResult, customLinks, data) => {
  // The render is only considered failed when one of the providers failed, and empty render is a valid case
  if (!customLinksProviderResult) {
    return;
  }
  const emptyRenderExpected = customLinks.length === 0;
  function getNotRenderedReason() {
    if ((customLinksProviderResult === null || customLinksProviderResult === void 0 ? void 0 : customLinksProviderResult.status) === Status.ERROR) {
      return 'custom_links_api_error';
    }
    const error = userSiteDataProviderResult.error;
    if (!error) {
      return null;
    }
    if (error instanceof UserSiteDataError) {
      return error.reason;
    } else {
      return 'usd_unknown';
    }
  }
  const notRenderedReason = getNotRenderedReason();
  return renderTrackerWithReason({
    subject: SWITCHER_CUSTOM_LINKS,
    notRenderedReason,
    emptyRenderExpected,
    data
  });
};

/**
 *
 * ***IMPORTANT*** DO NOT send PD / PII or any sensitive data.
 * This function defines analytic event attributes.
 *
 * @param groupIndex
 * @param id
 * @param type
 * @param productType
 * @param extraAttributes
 */
export const getItemAnalyticsContext = (groupIndex, id, type, productType, extraAttributes) => ({
  ...analyticsAttributes({
    groupIndex,
    itemId: id,
    itemType: type,
    productType,
    ...extraAttributes
  })
});
export { withAnalyticsEvents, NavigationAnalyticsContext, OPERATIONAL_EVENT_TYPE, UI_EVENT_TYPE };